import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  ACCOUNT_DETAIL_TYPE,
  AccountType,
  API_KEY_MAP,
  ContactType,
  HEROKU_ID_KEY,
  MembershipType,
  NEW_MEMBER_CONTACT_ACTION,
} from 'types'
import { pure } from 'recompose'
import { useHistory } from 'react-router-dom'
import { SubmissionError } from 'redux-form'
import useAuth from 'components/auth/useAuth'
import { MemberContactInfoForm } from 'member-portal/forms'
import {
  COUNTRY,
  PATH,
  makeMemberApplicationApplyPath,
  makeMembershipsPath,
  makePath,
} from 'config'
import * as LS from 'local-storage'
import {
  apiValuesWithMappedKeys,
  emailsMatch,
  getNewMemberContactAction,
  normalizePhoneNumbers,
  stripNoneTrailerCareType,
  useConfirm,
  handleSubmitFailWithFlashMessage,
} from 'utils'

const propTypes = {
  autoCompleteAddress: PropTypes.func.isRequired,
  clearShoppingCart: PropTypes.func.isRequired,
  createMembershipApplicationAccount: PropTypes.func.isRequired,
  fetchPlaceDetails: PropTypes.func.isRequired,
  lookupExistingContact: PropTypes.func.isRequired,
  prefillAccount: AccountType,
  prefillContact: ContactType,
  prefillMembership: MembershipType,
  selectedTrailerCareType: PropTypes.string.isRequired,
  shoppingCartMembershipType: PropTypes.string.isRequired,
  successPath: PropTypes.string.isRequired,
  updateMembershipApplicationAccount: PropTypes.func.isRequired,
}

function NewMemberContactInfo({
  autoCompleteAddress,
  clearShoppingCart,
  createMembershipApplicationAccount,
  fetchPlaceDetails,
  lookupExistingContact,
  prefillAccount = null,
  prefillContact = null,
  prefillMembership = null,
  selectedTrailerCareType,
  shoppingCartMembershipType,
  successPath,
  updateMembershipApplicationAccount,
}) {
  const history = useHistory()
  const { isAuthenticated, login, user } = useAuth()
  const { isConfirmed: confirmModal } = useConfirm()

  // Only resume the saved form once the user is signed in — an abandoned
  // login shouldn't leak the previous user's info to the next visitor.
  const [savedFormValues] = useState(() => {
    const saved = LS.getNewMemberContactFormValues()
    if (saved) {
      LS.clearNewMemberContactFormValues()
      if (!isAuthenticated) LS.clearExistingContactInfo()
    }
    return isAuthenticated ? saved : null
  })
  // Lookup response saved before sign-in so the post-login submit can reuse
  // the IDs. Only set on the email + phone match path.
  const [resumedExistingContact] = useState(() =>
    isAuthenticated && savedFormValues
      ? LS.getExistingContactInfo() ?? null
      : null,
  )

  return (
    <MemberContactInfoForm
      autoCompleteAddress={autoCompleteAddress}
      // Email is the lookup key, so locking it for signed-in users keeps
      // a stray edit from updating someone else's account.
      disableEmail={isAuthenticated}
      fetchPlaceDetails={fetchPlaceDetails}
      initialValues={{
        account: {
          country: COUNTRY.UNITED_STATES,
          accountDetailType: ACCOUNT_DETAIL_TYPE.CUSTOMER_RETAIL,
          ...savedFormValues?.account,
        },
        contact: normalizePhoneNumbers({
          email:
            (isAuthenticated && user?.email) ||
            savedFormValues?.contact?.email ||
            prefillContact?.email ||
            '',
          firstName:
            savedFormValues?.contact?.firstName ||
            prefillContact?.firstname ||
            '',
          lastName:
            savedFormValues?.contact?.lastName ||
            prefillContact?.lastname ||
            '',
          mobilePhone:
            savedFormValues?.contact?.mobilePhone ||
            prefillContact?.mobilephone ||
            prefillContact?.phone ||
            '',
        }),
      }}
      onSubmit={async (formValues) => {
        const { email, mobilePhone } = formValues.contact

        // `existingContact` sources, by priority:
        //  1. Pre-sign-in lookup snapshot (email + phone match has IDs),
        //     only when Auth0 email matches the saved lookup/form email.
        //  2. Dashboard prefill fills IDs for email-only Auth0 returns
        //     (lookup withheld them), mismatched Auth0 user, and email-only
        //     login without LS.
        //  3. Fresh lookup (unauthenticated submit).
        const hasPrefillIds = !!(
          prefillAccount?.[HEROKU_ID_KEY] && prefillContact?.[HEROKU_ID_KEY]
        )
        const prefillExistingContact = hasPrefillIds
          ? {
              contact_exists: true,
              account_hc_ext_id: prefillAccount[HEROKU_ID_KEY],
              contact_hc_ext_id: prefillContact[HEROKU_ID_KEY],
              membership_hc_ext_id: prefillMembership?.[HEROKU_ID_KEY],
            }
          : null
        const trustLookupIds =
          !!resumedExistingContact &&
          emailsMatch(user?.email, savedFormValues?.contact?.email)

        let existingContact
        if (trustLookupIds) {
          existingContact = {
            ...resumedExistingContact,
            account_hc_ext_id:
              resumedExistingContact.account_hc_ext_id ||
              prefillExistingContact?.account_hc_ext_id,
            contact_hc_ext_id:
              resumedExistingContact.contact_hc_ext_id ||
              prefillExistingContact?.contact_hc_ext_id,
            membership_hc_ext_id:
              resumedExistingContact.membership_hc_ext_id ||
              prefillExistingContact?.membership_hc_ext_id,
          }
        } else if (prefillExistingContact) {
          existingContact = prefillExistingContact
        } else if (isAuthenticated) {
          // Already signed in with no attach IDs (refresh after LS clear,
          // typed A / signed in as B, prefill not loaded). Do not run the
          // guest lookup — that would show Sign in / Join via App / Renew
          // and start another Auth0 trip or clear the cart.
          throw new SubmissionError({
            _error:
              'Unable to load your account. Please try again in a moment.',
          })
        } else {
          existingContact = await lookupExistingContact({
            email,
            phone_number: mobilePhone,
          })
        }

        const hasResolvedIds = !!(
          existingContact?.account_hc_ext_id &&
          existingContact?.contact_hc_ext_id
        )

        const action = await getNewMemberContactAction({
          existingContact,
          skipModals: !!(resumedExistingContact || (isAuthenticated && hasResolvedIds)),
          confirmModal,
        })

        const accountApiValues = apiValuesWithMappedKeys(
          formValues.account,
          API_KEY_MAP.ACCOUNT_KEY_MAP,
        )
        const contactApiValues = apiValuesWithMappedKeys(
          formValues.contact,
          API_KEY_MAP.CONTACT_KEY_MAP,
        )
        const membership = {
          shopping_cart_membership_type__c: shoppingCartMembershipType,
          shopping_cart_trailer_care_type__c: stripNoneTrailerCareType(
            selectedTrailerCareType,
          ),
        }

        switch (action) {
          case NEW_MEMBER_CONTACT_ACTION.CANCEL:
            return

          case NEW_MEMBER_CONTACT_ACTION.RENEW:
            login({
              appState: {
                returnTo: makeMembershipsPath(
                  PATH.SELECT_YOUR_MEMBERSHIP,
                  PATH.RENEW_MEMBERSHIP,
                ),
              },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.CREATE_LOGIN:
            // Membership on file but no login yet. Send them to the login page
            // to create one (the membership associates by email), then land
            // them in the member portal.
            login({
              appState: { returnTo: PATH.MEMBER_PORTAL },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.SIGN_IN:
            // Save form + lookup so Contact can prefill and attach after
            // Auth0. A successful signed-in save clears the lookup; later
            // steps do not read auth0_exists from it. Confirmation uses
            // the lookup only when still signed out (Proceed as Guest).
            LS.setNewMemberContactFormValues(formValues)
            if (existingContact) {
              LS.setExistingContactInfo(existingContact)
            }
            login({
              appState: {
                returnTo: makeMemberApplicationApplyPath(PATH.CONTACT),
              },
            })
            return

          case NEW_MEMBER_CONTACT_ACTION.EXIT_TO_MARKETING:
            // User chose to abandon the join and finish via the mobile app.
            clearShoppingCart()
            window.location.replace(makePath(PATH.SEATOW))
            return

          case NEW_MEMBER_CONTACT_ACTION.UPDATE:
            if (
              !existingContact?.account_hc_ext_id ||
              !existingContact?.contact_hc_ext_id
            ) {
              throw new SubmissionError({
                _error:
                  'Unable to load your account. Please try again in a moment.',
              })
            }
            // Drives the "Log In" CTA on the confirmation page; only the
            // unauthenticated guest path needs it.
            if (!isAuthenticated) LS.setExistingContactInfo(existingContact)
            await updateMembershipApplicationAccount({
              account: {
                [HEROKU_ID_KEY]: existingContact.account_hc_ext_id,
                ...accountApiValues,
              },
              contact: {
                [HEROKU_ID_KEY]: existingContact.contact_hc_ext_id,
                ...contactApiValues,
              },
              membership: {
                ...(existingContact.membership_hc_ext_id && {
                  [HEROKU_ID_KEY]: existingContact.membership_hc_ext_id,
                }),
                ...membership,
              },
            })
            // Logged-in save succeeded — drop the Sign-in lookup snapshot so
            // a later Edit visit does not treat it as another attach.
            if (isAuthenticated) LS.clearExistingContactInfo()
            return history.push(successPath)

          case NEW_MEMBER_CONTACT_ACTION.CREATE:
            await createMembershipApplicationAccount({
              account: accountApiValues,
              contact: contactApiValues,
              membership,
            })
            return history.push(successPath)

          default:
            throw new SubmissionError({
              _error: 'Something went wrong. Please try again.',
            })
        }
      }}
      onSubmitFail={handleSubmitFailWithFlashMessage}
    />
  )
}

NewMemberContactInfo.propTypes = propTypes

export default pure(NewMemberContactInfo)
